import { ReactNode, forwardRef } from 'react';

import { baseInputClassNames } from '@tg-web/components';
import { PropsWithCN, cn } from '@tg-web/utils';

import { IslandCollapse } from './IslandCollapse';

export type IslandInputCellProps = {
  /**
   * Length at which the character counter appears. Needs `maxLength` and
   * `valueLength` too — all three or none.
   */
  counterFrom?: number;
  /** Rendered under the input, inside the cell. */
  error?: string;
  leftIcon?: ReactNode;
  /**
   * Current length of the field, for the counter. Passed in rather than read
   * off `value` because the form owns the value: the caller already has it
   * from `form.watch`, and a cell that measured its own prop would disagree
   * with the form during a controlled update.
   */
  valueLength?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> &
  PropsWithCN;

/**
 * A 56px row whose content is a text input.
 *
 * The input is bare rather than the `Input` component on purpose: `Input` wraps
 * itself in a width-less relative div, which shrinks to its content as a flex
 * child and overflows the row. The error line lives inside the cell for the
 * same reason it does in `AttachLinksField` — a sibling below would be a
 * separate child of the island.
 */
export const IslandInputCell = forwardRef<
  HTMLInputElement,
  IslandInputCellProps
>(
  (
    {
      className,
      counterFrom,
      error,
      leftIcon,
      maxLength,
      valueLength,
      ...props
    },
    ref,
  ) => {
    // Destructured out of the spread only so it can also be the counter's
    // denominator; it still reaches the input below.
    const showCounter =
      maxLength !== undefined &&
      counterFrom !== undefined &&
      valueLength !== undefined &&
      valueLength >= counterFrom;

    return (
      <div
        // Always top-aligned with the same 15px gutter, so an error appearing
        // only adds its own height instead of also switching the row's layout.
        // One line still measures exactly 56px: 15 + (3 + 20 + 3) + 15.
        className={cn(
          'flex min-h-14 w-full items-start px-4 py-[15px]',
          className,
        )}
      >
        {leftIcon && (
          <div className="text-tg-section-header-text mr-[18px] flex h-[26px] w-[26px] shrink-0 items-center justify-center">
            {leftIcon}
          </div>
        )}
        <div className="min-w-0 flex-1 py-[3px]">
          <input
            className={cn(
              baseInputClassNames,
              'typo-text text-tg-text placeholder:text-tg-hint h-auto w-full rounded-none border-0 bg-transparent p-0',
            )}
            aria-invalid={Boolean(error)}
            maxLength={maxLength}
            ref={ref}
            {...props}
          />
          <IslandCollapse open={Boolean(error)}>
            <p className="text-tg-destructive-text typo-small mt-1.5">
              {error}
            </p>
          </IslandCollapse>
        </div>
        {showCounter && (
          // A 26px box beside the input rather than under it, so the row still
          // measures 56px while the counter is up: it matches the content
          // column's own first line, which is 3 + 20 + 3.
          //
          // `maxLength` caps typing but not a value that arrived over the limit
          // already -- an event whose title predates the limit opens here -- so
          // the counter has to be able to read past its own maximum, and says so
          // in the destructive colour when it does.
          <div className="ml-3 flex h-[26px] shrink-0 items-center">
            <span
              className={cn(
                'typo-feed text-tg-hint tabular-nums',
                valueLength > maxLength && 'text-tg-destructive-text',
              )}
            >
              {valueLength}/{maxLength}
            </span>
          </div>
        )}
      </div>
    );
  },
);

IslandInputCell.displayName = 'IslandInputCell';
